<template>
  <section class="w-full bg-white text-heading">
    <div
      v-if="isPreview && pageReady"
      class="cf-preview-bar text-white text-center text-xs sm:text-sm font-semibold py-2 px-4"
    >
      Preview — {{ apiFair && apiFair.is_published ? "this page is live" : "not visible to the public until you publish" }}
    </div>

    <div v-if="loading" class="py-24 px-5">
      <div class="max-w-6xl mx-auto">
        <div class="h-64 bg-gray-100 animate-pulse rounded-xl mb-8"></div>
        <div class="h-8 bg-gray-100 animate-pulse rounded w-1/2 mx-auto mb-4"></div>
        <div class="h-4 bg-gray-100 animate-pulse rounded w-2/3 mx-auto"></div>
      </div>
    </div>

    <div v-else-if="unavailable" class="py-24 px-5">
      <div class="max-w-xl mx-auto text-center">
        <p class="text-xs font-bold uppercase tracking-widest text-six mb-3">Career Fair</p>
        <h1 class="text-3xl font-extrabold text-heading mb-3">{{ unavailableTitle }}</h1>
        <p class="text-slate-500 leading-relaxed m-0">{{ unavailableMessage }}</p>
      </div>
    </div>

    <template v-else-if="pageReady">
    <!-- Hero -->
    <CareerFairHeroBlock
      v-if="showBannerHero || showComposedHero"
      :layout="hero.layout"
      :banner-url="bannerUrl"
      :banner-alt="heroAlt"
      :kicker="hero.kicker || ''"
      :title="hero.title || ''"
      :tagline="hero.tagline || ''"
      :hashtag="hero.hashtag || ''"
      :buttons="heroButtons"
      :circle-urls="heroCircles"
      variant="desktop"
      :interactive="true"
      @navigate="handleHeroButton"
    />

    <!-- Title bar -->
    <div class="bg-four py-4 px-5 text-center">
        <h2 class="m-0 text-base font-bold uppercase tracking-widest text-gray-800">
          {{ displayTitle }}
        </h2>
    </div>

    <!-- Event meta + intro + feature image -->
    <div class="py-11">
      <div class="w-full max-w-6xl mx-auto px-5 text-center">
        <p class="text-lg mb-5 text-heading">
          <strong>{{ fair.dateLabel }}</strong>
          <span class="mx-2 text-slate-500">|</span>
          {{ fair.timeLabel }}
          <span class="mx-2 text-slate-500">|</span>
          {{ fair.locationLabel }}
        </p>
        <p class="text-slate-500 leading-relaxed max-w-3xl mx-auto mb-7 text-base">
          {{ fair.intro }}
        </p>
        <div class="mx-auto max-w-4xl overflow-hidden shadow-xl">
          <img :src="images.fairHall" alt="Career fair exhibition hall" class="block w-full h-auto max-h-96 object-cover" />
        </div>
      </div>
    </div>

    <!-- Quick nav cards -->
    <div class="pt-6 pb-11">
      <div class="w-full max-w-6xl mx-auto px-5">
        <div class="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3">
          <a
            v-for="card in fair.navCards"
            :key="card.id"
            :href="'#' + card.id"
            class="group relative flex items-center justify-center min-h-28 lg:min-h-32 bg-cover bg-center shadow-lg no-underline overflow-hidden transition-transform duration-300 hover:-translate-y-1 hover:shadow-2xl"
            :style="{ backgroundImage: 'url(' + navImage(card.imageKey) + ')' }"
            @click.prevent="scrollTo(card.id)"
          >
            <span class="absolute inset-0 bg-ternary bg-opacity-60 group-hover:bg-opacity-45 transition-colors duration-300"></span>
            <span class="relative z-10 text-white font-bold text-sm text-center px-3 py-3 leading-snug">
              {{ card.label }}
            </span>
          </a>
        </div>
      </div>
    </div>

    <!-- Sponsors -->
    <div id="sponsors" class="scroll-mt-4">
      <div class="bg-four py-3 px-5 text-center">
        <h2 class="m-0 text-base font-bold uppercase tracking-widest text-gray-800">
          IAT Career Fair Sponsors
        </h2>
      </div>
      <div class="py-6 px-5">
        <div class="w-full max-w-6xl mx-auto">
          <div class="bg-white border border-light-gray shadow-md overflow-hidden">
            <div
              v-for="(tier, tIndex) in sponsorTiers"
              :key="tier.key"
              class="px-4 py-4 sm:px-6 sm:py-5"
              :class="tIndex < sponsorTiers.length - 1 ? 'border-b border-light-gray' : ''"
            >
              <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
                <div class="sm:w-36 shrink-0">
                  <span
                    class="inline-block text-xs font-bold uppercase tracking-wider text-six border-b-2 border-six pb-1"
                  >{{ tier.shortLabel }}</span>
                </div>
                <div class="flex-1 w-full" :class="sponsorGridClass(tier.key)">
                  <div
                    v-for="(sponsor, sIndex) in tier.items"
                    :key="tier.key + '-' + sIndex"
                    class="flex flex-col items-center justify-center bg-four border border-light-gray text-center transition-all duration-200 hover:border-six hover:shadow-md"
                    :class="sponsorItemClass(tier.key)"
                  >
                    <span
                      class="font-extrabold text-primary tracking-wide leading-none"
                      :class="sponsorInitialsClass(tier.key)"
                    >{{ sponsor.initials }}</span>
                    <span class="mt-1 text-xs text-slate-500 leading-tight">{{ sponsor.name }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p class="text-center text-xs text-slate-500 mt-4">
            Sponsor logos are placeholders — final branding will be added before the event.
          </p>
        </div>
      </div>
    </div>

    <!-- Participating companies -->
    <div id="companies" class="scroll-mt-4">
      <div class="bg-four py-4 px-5 text-center">
        <h2 class="m-0 text-base font-bold uppercase tracking-widest text-gray-800">
          Participating Companies
        </h2>
      </div>
      <div class="py-11">
        <div class="w-full max-w-6xl mx-auto px-5">
          <div class="mx-auto max-w-4xl overflow-hidden shadow-xl mb-8">
            <img :src="images.companiesHall" alt="Participating companies at the fair" class="block w-full h-auto max-h-96 object-cover" />
          </div>
          <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3">
            <template v-for="(company, cIndex) in fair.companies" :key="'co-' + cIndex">
              <button
                v-if="hasCompanyLink(company)"
                type="button"
                class="text-left text-primary font-bold text-sm bg-transparent border-0 p-0 cursor-pointer hover:text-six hover:underline transition-colors duration-200"
                @click="openExternalLinkConfirm(company)"
              >
                {{ getCompanyName(company) }}
              </button>
              <span
                v-else
                class="text-primary font-bold text-sm"
              >{{ getCompanyName(company) }}</span>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- External link confirmation -->
    <div
      v-if="externalLinkConfirm"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      @click.self="closeExternalLinkConfirm"
    >
      <div class="w-full max-w-lg bg-white shadow-2xl overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="cf-leave-title">
        <div class="bg-primary text-white px-5 py-4">
          <h3 id="cf-leave-title" class="m-0 text-base font-semibold leading-snug">
            You are now leaving the IAT Alumni Portal website.
          </h3>
        </div>
        <div class="px-5 py-5">
          <p class="text-sm text-slate-600 leading-relaxed m-0 mb-4">
            By continuing, you will be taken to a website not affiliated with the Institute of Applied Technology.
            Links to external sites are provided only for users’ convenience and imply no endorsement of the site
            and/or its content. Note that the privacy policy and security settings of the linked site may differ
            from those of this website.
          </p>
          <p class="text-sm text-six font-medium break-all m-0">{{ externalLinkConfirm.url }}</p>
        </div>
        <div class="flex border-t border-light-gray">
          <button
            type="button"
            class="flex-1 px-4 py-3 text-sm font-semibold text-slate-600 bg-gray-100 border-0 border-r border-light-gray cursor-pointer hover:bg-gray-200 transition-colors duration-200"
            @click="closeExternalLinkConfirm"
          >
            Cancel
          </button>
          <button
            type="button"
            class="flex-1 px-4 py-3 text-sm font-semibold text-white bg-primary border-0 cursor-pointer hover:bg-primary-600 transition-colors duration-200 inline-flex items-center justify-center gap-2"
            @click="confirmExternalLink"
          >
            <span>Open link</span>
            <font-awesome-icon icon="fa-solid fa-external-link-alt" class="text-xs" />
          </button>
        </div>
      </div>
    </div>

    <!-- Image preview (campus map / route map) -->
    <div
      v-if="imageModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      @click.self="closeImageModal"
    >
      <div class="w-full max-w-4xl bg-white shadow-2xl overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="cf-image-title">
        <div class="flex items-center justify-between bg-primary text-white px-5 py-4">
          <h3 id="cf-image-title" class="m-0 text-base font-semibold">{{ imageModal.title }}</h3>
          <button
            type="button"
            class="text-white bg-transparent border-0 text-2xl leading-none cursor-pointer px-2 py-1 hover:text-six transition-colors duration-200"
            aria-label="Close"
            @click="closeImageModal"
          >
            &times;
          </button>
        </div>
        <div class="p-4 bg-white">
          <img
            :src="imageModal.src"
            :alt="imageModal.title"
            class="block w-full h-auto max-h-screen object-contain mx-auto"
          />
        </div>
        <div class="border-t border-light-gray px-5 py-3 flex justify-end">
          <button
            type="button"
            class="px-5 py-2 text-sm font-semibold text-white bg-primary border-0 cursor-pointer hover:bg-primary-600 transition-colors duration-200"
            @click="closeImageModal"
          >
            Close
          </button>
        </div>
      </div>
    </div>

    <!-- Visit us -->
    <div id="visit-us" class="scroll-mt-4">
      <div class="bg-four py-4 px-5 text-center">
        <h2 class="m-0 text-base font-bold uppercase tracking-widest text-gray-800">Visit Us</h2>
      </div>
      <div class="py-11">
        <div class="w-full max-w-6xl mx-auto px-5">
          <div class="text-center mb-3">
            <h3 class="text-2xl font-extrabold inline-block pb-2 border-b-2 border-six max-w-2xl">
              {{ fair.visitUs.title }} – Spring {{ fair.year }}!
            </h3>
          </div>
          <p class="font-bold text-center mb-4">{{ fair.visitUs.exclusiveNote }}</p>
          <p class="text-slate-500 leading-relaxed max-w-3xl mx-auto mb-7 text-base">
            {{ fair.visitUs.body }}
          </p>

          <div class="grid grid-cols-1 sm:grid-cols-3 gap-5 my-8">
            <div
              v-for="(item, hIndex) in fair.visitUs.highlights"
              :key="'hl-' + hIndex"
              class="bg-gray-100 overflow-hidden"
            >
              <div
                class="h-40 bg-cover bg-center"
                :style="{ backgroundImage: 'url(' + highlightImages[hIndex] + ')' }"
              ></div>
              <h4 class="text-sm font-extrabold uppercase tracking-wide mx-4 mt-4 mb-2">{{ item.title }}</h4>
              <p class="text-slate-500 text-sm leading-relaxed mx-4 mb-5">{{ item.text }}</p>
            </div>
          </div>

          <div class="mt-8 mx-auto max-w-md border border-primary text-center">
            <a
              :href="fair.registerUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="block px-5 py-4 text-primary font-bold no-underline hover:bg-primary hover:text-white transition-colors duration-200"
            >
              {{ fair.registerCta }}
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Exhibit -->
    <div id="exhibit" class="scroll-mt-4">
      <div class="bg-four py-4 px-5 text-center">
        <h2 class="m-0 text-base font-bold uppercase tracking-widest text-gray-800">Exhibit With Us</h2>
      </div>
      <div class="py-11">
        <div class="w-full max-w-6xl mx-auto px-5">
          <div class="mx-auto max-w-4xl overflow-hidden shadow-xl mb-7">
            <img :src="images.exhibit" alt="Exhibit with us at the career fair" class="block w-full h-auto max-h-96 object-cover" />
          </div>
          <p class="text-slate-500 leading-relaxed max-w-3xl mx-auto mb-7 text-base text-center">
            {{ fair.exhibit.intro }}
          </p>

          <h3 class="text-2xl font-extrabold mb-4 pb-2 border-b-2 border-six w-full text-left">
            Benefits of Participating
          </h3>
          <div class="my-6 mb-8">
            <div
              v-for="(benefit, bIndex) in fair.exhibit.benefits"
              :key="'bn-' + bIndex"
              class="mb-5"
            >
              <h4 class="font-extrabold mb-1">{{ benefit.title }}</h4>
              <p class="text-slate-500 leading-relaxed m-0">{{ benefit.text }}</p>
            </div>
          </div>

          <div class="flex justify-center">
            <a
              :href="fair.exhibit.reserveUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center justify-center px-5 py-3 text-sm font-bold no-underline border border-primary text-primary bg-white hover:bg-primary hover:text-white transition-colors duration-200"
            >
              {{ fair.exhibit.cta }}
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Getting ready -->
    <div id="getting-ready" class="scroll-mt-4">
      <div class="bg-four py-4 px-5 text-center">
        <h2 class="m-0 text-base font-bold uppercase tracking-widest text-gray-800">
          Getting Ready for Career Fair
        </h2>
      </div>
      <div class="py-11">
        <div class="w-full max-w-6xl mx-auto px-5">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">
            <div>
              <h3 class="text-xl font-extrabold mb-3">Attend the workshop</h3>
              <p class="text-slate-500 leading-relaxed mb-4">{{ fair.workshops.intro }}</p>
              <div
                v-for="(ws, wIndex) in fair.workshops.items"
                :key="'ws-' + wIndex"
                class="mt-5"
              >
                <h4 class="font-extrabold mb-1">{{ ws.title }}</h4>
                <p class="text-slate-500 leading-relaxed m-0">{{ ws.text }}</p>
              </div>
            </div>
            <div>
              <h3 class="text-xl font-extrabold mb-3">Schedule</h3>
              <p class="text-slate-500 leading-relaxed mb-4">{{ fair.workshops.scheduleIntro }}</p>
              <ul class="list-disc pl-5 mt-4">
                <li
                  v-for="(slot, sIndex) in fair.workshops.schedule"
                  :key="'sch-' + sIndex"
                  class="mb-2 leading-normal"
                >
                  <span>{{ slot.title }}</span>
                  <span class="mx-1 text-slate-500">|</span>
                  <strong>{{ slot.date }}</strong>
                </li>
              </ul>
              <a
                :href="fair.workshops.registerUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center justify-center px-5 py-3 mt-6 text-sm font-bold no-underline border border-primary text-primary bg-white hover:bg-primary hover:text-white transition-colors duration-200"
              >
                {{ fair.workshops.cta }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Jobs -->
    <div id="jobs" class="scroll-mt-4">
      <div class="bg-four py-4 px-5 text-center">
        <h2 class="m-0 text-base font-bold uppercase tracking-widest text-gray-800">Jobs</h2>
      </div>
      <div class="py-11">
        <div class="w-full max-w-6xl mx-auto px-5">
          <p class="text-slate-500 leading-relaxed max-w-3xl mx-auto mb-7 text-base text-center">
            {{ fair.jobs.text }}
          </p>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            <template v-for="(job, jIndex) in fair.jobs.placeholders" :key="'job-' + jIndex">
              <button
                v-if="job.url"
                type="button"
                class="text-left border border-light-gray p-5 bg-white w-full cursor-pointer hover:border-primary transition-all duration-200 hover:shadow-lg"
                @click="openExternalLinkConfirm(job)"
              >
                <span class="inline-block text-xs font-bold uppercase tracking-wide text-six mb-2">{{ job.type }}</span>
                <h4 class="text-lg font-extrabold mb-1">{{ job.title }}</h4>
                <p class="text-slate-500 m-0">{{ job.company }}</p>
              </button>
              <div
                v-else
                class="border border-light-gray p-5"
              >
                <span class="inline-block text-xs font-bold uppercase tracking-wide text-six mb-2">{{ job.type }}</span>
                <h4 class="text-lg font-extrabold mb-1">{{ job.title }}</h4>
                <p class="text-slate-500 m-0">{{ job.company }}</p>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Coming to campus -->
    <div id="campus" class="scroll-mt-4">
      <div class="bg-four py-4 px-5 text-center">
        <h2 class="m-0 text-base font-bold uppercase tracking-widest text-gray-800">
          Coming to Campus: What You Need to Know
        </h2>
      </div>
      <div class="py-11">
        <div class="w-full max-w-6xl mx-auto px-5">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-center">
            <div
              class="min-h-56 lg:min-h-72 bg-cover bg-center"
              :style="{ backgroundImage: 'url(' + images.campus + ')' }"
            ></div>
            <div>
              <p class="text-slate-500 leading-relaxed mb-5 text-left">{{ fair.campus.text }}</p>
              <div class="flex flex-wrap gap-3">
                <button
                  type="button"
                  class="inline-flex items-center justify-center px-5 py-3 text-sm font-bold no-underline border border-primary text-primary bg-white hover:bg-primary hover:text-white transition-colors duration-200 cursor-pointer"
                  @click="handleDirectionsClick"
                >
                  Directions to campus
                </button>
                <button
                  type="button"
                  class="inline-flex items-center justify-center px-5 py-3 text-sm font-bold no-underline border border-primary text-primary bg-white hover:bg-primary hover:text-white transition-colors duration-200 cursor-pointer"
                  @click="handleCampusMapClick"
                >
                  Campus map
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- FAQs -->
    <div id="faqs" class="scroll-mt-4">
      <div class="bg-four py-4 px-5 text-center">
        <h2 class="m-0 text-base font-bold uppercase tracking-widest text-gray-800">FAQs</h2>
      </div>
      <div class="py-11">
        <div class="w-full max-w-3xl mx-auto px-5">
          <div
            v-for="(group, gIndex) in fair.faqs"
            :key="'faq-g-' + gIndex"
            class="border-b border-light-gray"
          >
            <button
              type="button"
              class="w-full flex items-center justify-between gap-4 py-4 bg-transparent border-0 cursor-pointer text-lg font-bold text-heading text-left"
              :aria-expanded="openFaq === gIndex ? 'true' : 'false'"
              @click="toggleFaq(gIndex)"
            >
              <span>{{ group.category }}</span>
              <font-awesome-icon
                icon="fa-solid fa-chevron-down"
                class="cf-faq-chevron text-slate-500 text-sm"
                :class="{ 'cf-faq-chevron--open': openFaq === gIndex }"
              />
            </button>
            <div
              class="cf-faq-body"
              :class="{ 'cf-faq-body--open': openFaq === gIndex }"
            >
              <div class="cf-faq-inner">
                <div class="pb-5">
                  <div
                    v-for="(item, iIndex) in group.items"
                    :key="'faq-i-' + gIndex + '-' + iIndex"
                    class="mb-4"
                  >
                    <h4 class="font-bold mb-1">{{ item.q }}</h4>
                    <p class="text-slate-500 leading-relaxed m-0">{{ item.a }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Contact -->
    <div id="contact" class="scroll-mt-4">
      <div class="bg-four py-4 px-5 text-center">
        <h2 class="m-0 text-base font-bold uppercase tracking-widest text-gray-800">Contact Us</h2>
      </div>
      <div class="py-11 pb-14">
        <div class="w-full max-w-6xl mx-auto px-5 text-center">
          <p class="text-lg leading-relaxed mb-7">
            For more information, contact us on
            <a :href="'mailto:' + fair.contactEmail" class="text-twelve font-bold no-underline hover:underline">{{ fair.contactEmail }}</a>
            or
            <a :href="phoneTelLink(fair.contactPhone)" class="text-heading font-bold no-underline hover:underline">{{ fair.contactPhone }}</a>.
          </p>
          <div class="flex justify-center flex-wrap gap-8">
            <template v-for="(social, sIndex) in socialLinks" :key="'social-' + sIndex">
              <a
                v-if="social.url"
                :href="social.url"
                target="_blank"
                rel="noopener noreferrer"
                class="flex flex-col items-center gap-2 text-slate-500 text-sm no-underline hover:text-primary transition-colors duration-200"
              >
                <font-awesome-icon :icon="getSocialIcon(social.platform)" class="text-3xl text-primary" />
                <span>{{ social.label }}</span>
              </a>
            </template>
          </div>
        </div>
      </div>
    </div>
    </template>
  </section>
</template>

<script>
import gql from "graphql-tag";
import { getCareerFairData } from "./data";
import { mediaPublicUrl, CAREER_FAIR_FIELDS } from "../career-fair-management/graphql";
import CareerFairHeroBlock from "./components/CareerFairHeroBlock.vue";
import careerImg from "@/assets/images/career.jpeg";
import careerPng from "@/assets/images/career.png";
import eventImg from "@/assets/images/event.jpeg";
import eventsBg from "@/assets/images/events-bg.jpeg";
import campusImg from "@/assets/images/campus.png";
import schoolImg from "@/assets/images/school.jpeg";
import networkImg from "@/assets/images/network.jpeg";
import heroImg from "@/assets/images/hero1.jpeg";
import news1 from "@/assets/images/news1.jpeg";
import news2 from "@/assets/images/news2.jpeg";
import news3 from "@/assets/images/news3.jpeg";
import emp1 from "@/assets/images/employment/img1.jpg";
import emp2 from "@/assets/images/employment/img2.png";
import emp3 from "@/assets/images/employment/img3.png";

const PUBLIC_QUERY = gql`
  query PublicCareerFair($year: String!) {
    publicCareerFair(year: $year) {
      error
      message
      data { ${CAREER_FAIR_FIELDS} }
    }
  }
`;

const PREVIEW_QUERY = gql`
  query CareerFairPreview($year: String!) {
    careerFairPreview(year: $year) {
      error
      message
      data { ${CAREER_FAIR_FIELDS} }
    }
  }
`;

export default {
  name: "CareerFair",
  components: { CareerFairHeroBlock },
  props: {
    year: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      loading: true,
      unavailable: false,
      unavailableTitle: "",
      unavailableMessage: "",
      apiFair: null,
      openFaq: -1,
      externalLinkConfirm: null,
      imageModal: null,
      images: {
        fairHall: careerPng,
        companiesHall: eventsBg,
        exhibit: careerImg,
        campus: campusImg,
      },
      highlightImages: [emp1, emp2, emp3],
      navImageMap: {
        sponsors: news1,
        companies: careerPng,
        visit: schoolImg,
        exhibit: eventImg,
        ready: networkImg,
        jobs: emp1,
        campus: campusImg,
        faqs: news2,
        contact: news3,
      },
      campusImageMap: {
        campusMap: campusImg,
        routeMap: campusImg,
      },
    };
  },
  computed: {
    isPreview() {
      return !!(this.$route && this.$route.meta && this.$route.meta.isPreview);
    },
    pageReady() {
      return !this.loading && !this.unavailable && !!this.apiFair;
    },
    fair() {
      var routeYear = this.$route && this.$route.params && this.$route.params.year;
      var y = this.apiFair && this.apiFair.year ? this.apiFair.year : (this.year != null && this.year !== "" ? this.year : routeYear);
      return getCareerFairData(y);
    },
    displayTitle() {
      if (this.apiFair && this.apiFair.title) return this.apiFair.title;
      return "IAT Career Fair " + this.fair.year;
    },
    hero() {
      return this.apiFair && this.apiFair.hero ? this.apiFair.hero : null;
    },
    bannerUrl() {
      return this.hero && this.hero.bannerMedia ? mediaPublicUrl(this.hero.bannerMedia) : "";
    },
    showBannerHero() {
      return !!(this.hero && this.hero.layout === "banner" && this.bannerUrl);
    },
    heroCircles() {
      var urls = [];
      var h = this.hero;
      if (!h) return urls;
      var items = [h.circle1Media, h.circle2Media, h.circle3Media, h.circle4Media];
      var i;
      var url;
      for (i = 0; i < items.length; i++) {
        url = mediaPublicUrl(items[i]);
        if (url) urls.push(url);
      }
      return urls;
    },
    heroButtons() {
      var buttons = [];
      var h = this.hero;
      if (!h) return buttons;
      if (h.button_1_label) {
        buttons.push({ label: h.button_1_label, url: h.button_1_url || "" });
      }
      if (h.button_2_label) {
        buttons.push({ label: h.button_2_label, url: h.button_2_url || "" });
      }
      return buttons;
    },
    showComposedHero() {
      var h = this.hero;
      if (!h || h.layout === "banner") return false;
      return !!(h.kicker || h.title || h.tagline || h.hashtag || this.heroButtons.length || this.heroCircles.length);
    },
    heroAlt() {
      if (this.hero && this.hero.title) return this.hero.title;
      return this.displayTitle;
    },
    sponsorTiers() {
      var s = this.fair.sponsors;
      return [
        { key: "platinum", label: "Platinum Sponsors", shortLabel: "Platinum", items: s.platinum },
        { key: "gold", label: "Gold Sponsors", shortLabel: "Gold", items: s.gold },
        { key: "silver", label: "Silver Sponsors", shortLabel: "Silver", items: s.silver },
        { key: "bronze", label: "Bronze Sponsors", shortLabel: "Bronze", items: s.bronze },
        { key: "institutional", label: "Institutional Advancement Sponsors", shortLabel: "Institutional", items: s.institutional },
      ];
    },
    socialLinks() {
      return this.fair && this.fair.socialLinks ? this.fair.socialLinks : [];
    },
  },
  watch: {
    "$route.params.year": function () {
      this.openFaq = -1;
      this.scrollPageToTop();
      this.fetchPage();
    },
    isPreview: function () {
      this.fetchPage();
    },
  },
  created() {
    this.fetchPage();
  },
  methods: {
    async fetchPage() {
      this.loading = true;
      this.unavailable = false;
      this.unavailableTitle = "";
      this.unavailableMessage = "";
      this.apiFair = null;
      var routeYear = this.$route && this.$route.params && this.$route.params.year;
      var year = String(this.year != null && this.year !== "" ? this.year : routeYear || "");
      try {
        var result = await this.$apollo.query({
          query: this.isPreview ? PREVIEW_QUERY : PUBLIC_QUERY,
          variables: { year: year },
          fetchPolicy: "network-only",
        });
        var payload = this.isPreview
          ? (result.data && result.data.careerFairPreview)
          : (result.data && result.data.publicCareerFair);
        if (!payload || payload.error || !payload.data) {
          this.unavailable = true;
          if (this.isPreview) {
            this.unavailableTitle = "Preview not available";
            this.unavailableMessage = "This career fair has not been created yet.";
          } else {
            this.unavailableTitle = "Page not available";
            this.unavailableMessage = "This career fair is not published, or it does not exist.";
          }
        } else {
          this.apiFair = payload.data;
        }
      } catch (e) {
        this.unavailable = true;
        if (this.isPreview) {
          this.unavailableTitle = "Preview not available";
          this.unavailableMessage = "This career fair has not been created yet.";
        } else {
          this.unavailableTitle = "Page not available";
          this.unavailableMessage = "This career fair is not published, or it does not exist.";
        }
      } finally {
        this.loading = false;
      }
    },
    isHashUrl(url) {
      return !!(url && String(url).charAt(0) === "#");
    },
    handleHeroButton(url) {
      if (!this.isHashUrl(url)) return;
      this.scrollTo(String(url).substring(1));
    },
    navImage(key) {
      return this.navImageMap[key] || heroImg;
    },
    sponsorGridClass(tierKey) {
      if (tierKey === "bronze") {
        return "grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2";
      }
      if (tierKey === "silver") {
        return "grid grid-cols-1 sm:grid-cols-3 gap-3";
      }
      if (tierKey === "institutional") {
        return "grid grid-cols-2 gap-3 max-w-lg";
      }
      return "flex flex-wrap gap-3 justify-center sm:justify-start";
    },
    sponsorItemClass(tierKey) {
      if (tierKey === "platinum" || tierKey === "gold") {
        return "px-6 py-4 w-40 sm:w-44";
      }
      if (tierKey === "silver") {
        return "px-4 py-3";
      }
      if (tierKey === "bronze") {
        return "px-2 py-2";
      }
      return "px-4 py-3";
    },
    sponsorInitialsClass(tierKey) {
      if (tierKey === "platinum" || tierKey === "gold") {
        return "text-2xl";
      }
      if (tierKey === "silver") {
        return "text-xl";
      }
      return "text-sm";
    },
    getScrollContainer() {
      return document.getElementById("default-scroll-container");
    },
    scrollPageToTop() {
      var container = this.getScrollContainer();
      if (container) {
        container.scrollTop = 0;
      } else {
        window.scrollTo(0, 0);
      }
    },
    scrollTo(id) {
      var el = document.getElementById(id);
      var container = this.getScrollContainer();
      if (el && container) {
        var containerRect = container.getBoundingClientRect();
        var elRect = el.getBoundingClientRect();
        var top = container.scrollTop + (elRect.top - containerRect.top) - 8;
        container.scrollTo({ top: top, behavior: "smooth" });
      } else if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    toggleFaq(index) {
      this.openFaq = this.openFaq === index ? -1 : index;
    },
    phoneTelLink(phone) {
      if (!phone) return "";
      return "tel:" + String(phone).replace(/\s/g, "");
    },
    getSocialIcon(platform) {
      var icons = {
        instagram: ["fab", "instagram"],
        linkedin: ["fab", "linkedin"],
        facebook: ["fab", "facebook"],
        youtube: ["fab", "youtube"],
        x: ["fab", "x-twitter"],
        twitter: ["fab", "x-twitter"],
      };
      var key = platform ? String(platform).toLowerCase() : "";
      return icons[key] || ["fas", "link"];
    },
    getCompanyName(company) {
      if (!company) return "";
      if (typeof company === "string") return company;
      return company.name || "";
    },
    hasCompanyLink(company) {
      return company && typeof company === "object" && company.url;
    },
    openExternalLinkConfirm(item) {
      if (!item || !item.url) return;
      this.externalLinkConfirm = {
        url: item.url,
      };
    },
    closeExternalLinkConfirm() {
      this.externalLinkConfirm = null;
    },
    confirmExternalLink() {
      if (!this.externalLinkConfirm || !this.externalLinkConfirm.url) {
        this.closeExternalLinkConfirm();
        return;
      }
      window.open(this.externalLinkConfirm.url, "_blank", "noopener,noreferrer");
      this.closeExternalLinkConfirm();
    },
    getCampusImage(imageKey) {
      if (imageKey && this.campusImageMap[imageKey]) {
        return this.campusImageMap[imageKey];
      }
      return this.images.campus;
    },
    handleDirectionsClick() {
      var directions = this.fair.campus && this.fair.campus.directions;
      if (!directions) return;
      if (directions.type === "url") {
        if (!directions.value) return;
        window.open(directions.value, "_blank", "noopener,noreferrer");
        return;
      }
      if (directions.type === "image") {
        this.openImageModal("Directions to campus", this.getCampusImage(directions.imageKey || "routeMap"));
      }
    },
    handleCampusMapClick() {
      var campusMap = this.fair.campus && this.fair.campus.campusMap;
      var imageKey = campusMap && campusMap.imageKey ? campusMap.imageKey : "campusMap";
      this.openImageModal("Campus map", this.getCampusImage(imageKey));
    },
    openImageModal(title, src) {
      if (!src) return;
      this.imageModal = {
        title: title,
        src: src,
      };
    },
    closeImageModal() {
      this.imageModal = null;
    },
  },
};
</script>

<style lang="scss" scoped>
/* Legacy Tailwind cannot express these — keep minimal scoped CSS only */
.cf-hero-banner {
  overflow: hidden;
}

.cf-preview-bar {
  background: linear-gradient(90deg, #394867 0%, #499DD4 100%);
}

.cf-year-gradient {
  background: linear-gradient(90deg, #fff 0%, #499DD4 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

/* FAQ accordion — height + chevron (not expressible reliably in legacy Tailwind) */
.cf-faq-chevron {
  transition: transform 0.3s ease;
}

.cf-faq-chevron--open {
  transform: rotate(180deg);
}

.cf-faq-body {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.35s ease;
}

.cf-faq-body--open {
  grid-template-rows: 1fr;
}

.cf-faq-inner {
  overflow: hidden;
  min-height: 0;
}
</style>
